import {
  Component,
  HostBinding,
  Injector,
  Type,
  ViewContainerRef,
  computed,
  effect,
  inject,
  model,
  untracked,
  viewChild,
} from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  AutomationTrigger,
  AutomationTriggerConfig,
  IAutomationData,
} from '@kinet-open-network/shared/data-access/model';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { Subject, takeUntil } from 'rxjs';
import { AutomationTriggerService } from './automation-trigger.service';
import {
  AUTOMATION_TOKEN,
  TriggerComponent,
} from './triggers/abstract-trigger.component';

@Component({
  selector: 'kon-automation-trigger-container',
  imports: [SelectModule, FormsModule, ButtonModule],
  template: ` <ng-template #vcr /> `,
})
export class AutomationTriggerContainerComponent {
  readonly #triggerService = inject(AutomationTriggerService);

  readonly #recreateStream = new Subject<void>();

  readonly vcr = viewChild('vcr', { read: ViewContainerRef });

  readonly automation = model<Partial<IAutomationData>>({});
  readonly triggerType = computed(() => this.automation().trigger);
  readonly triggerData = computed(() => this.automation().triggerData);

  @HostBinding('class')
  readonly classes = 'flex flex-col p-2 border rounded-lg';

  private triggerComponentInstance: TriggerComponent<AutomationTrigger> | null =
    null;

  handleDataChange(data: AutomationTriggerConfig<AutomationTrigger>) {
    this.automation.set({
      ...this.automation(),
      triggerData: data,
    });
  }

  constructor() {
    effect(() => {
      const type = this.triggerType();
      const vcr = this.vcr();

      if (!vcr) {
        this.triggerComponentInstance = null;
        return;
      }

      if (
        this.triggerComponentInstance &&
        type === this.triggerComponentInstance.automation.trigger
      ) {
        return;
      }

      const component = this.#triggerService.getComponentForType(type);

      untracked(() => {
        this.recreateComponent(component, vcr);
      });
    });
  }

  private recreateComponent(
    componentType: Type<TriggerComponent<AutomationTrigger>>,
    vcr: ViewContainerRef,
  ) {
    vcr.clear();
    this.#recreateStream.next();
    this.triggerComponentInstance = null;

    const injector = Injector.create({
      providers: [
        {
          provide: AUTOMATION_TOKEN,
          useValue: this.automation(),
        },
      ],
      parent: vcr.injector,
    });

    const componentRef = vcr.createComponent(componentType, { injector });
    const componentInstance = componentRef.instance;

    outputToObservable(componentInstance.triggerChange)
      .pipe(takeUntil(this.#recreateStream))
      .subscribe((data) => {
        this.handleDataChange(data);
      });

    this.triggerComponentInstance = componentInstance;

    componentRef.changeDetectorRef.detectChanges();
  }
}
